// src/components/CustomSelect.tsx

import React, { useState, useMemo } from 'react';
import { BaseDropdown } from './BaseDropdown';
import { CustomSelectProps, ExtendedDropdownOption, OptionGroup } from '../types/Dropdown';
import { Lock } from 'react-feather';
import './CustomSelect.scss';

export const CustomSelect: React.FC<CustomSelectProps> = ({
    options,
    value,
    onChange,
    placeholder = 'Select option',
    disabled = false,
    className = '',
    groups,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const selectedOption = options.find(option => option.value === value);

    const groupedOptions = useMemo(() => {
        if (!groups) return { ungrouped: options };

        const groupedData: { [key: string]: ExtendedDropdownOption[] } = {};
        const processedOptions = new Set<ExtendedDropdownOption>();

        // Process each group in order
        groups.forEach(group => {
            groupedData[group.id] = options.filter(option => {
                if (group.filter(option)) {
                    processedOptions.add(option);
                    return true;
                }
                return false;
            });
        });

        // Collect any unprocessed options
        const ungrouped = options.filter(option => !processedOptions.has(option));
        if (ungrouped.length > 0) {
            groupedData.ungrouped = ungrouped;
        }

        return groupedData;
    }, [options, groups]);

    const handleSelect = (optionValue: string | undefined) => {
        onChange(optionValue);
        setIsOpen(false);
    };

    const renderOptionContent = (option: ExtendedDropdownOption) => (
        <div className="select-option-content">
            <span className="select-label">{option.label}</span>
            {option.metadata?.leftInfo && (
                <div
                    className="select-left-info"
                    dangerouslySetInnerHTML={{ __html: option.metadata.leftInfo }}
                />
            )}
            {option.metadata?.rightInfo && (
                <span className="select-right-info">{option.metadata.rightInfo}</span>
            )}
            {option.is_default && groups && (
                <Lock size={14} className="lock-icon" />
            )}
        </div>
    );

    const menuContent = groups ? (
        <div className="grouped-content">
            {groups.map(group => (
                groupedOptions[group.id]?.length > 0 && (
                    <div key={group.id} className="option-group">
                        <div className="option-group-label">{group.label}</div>
                        {groupedOptions[group.id].map((option, index) => (
                            <div
                                key={`${group.id}-${index}`}
                                className={`select-option ${option.value === value ? 'selected' : ''} ${option.is_default ? 'default' : ''}`}
                                onClick={() => handleSelect(option.value)}
                            >
                                {renderOptionContent(option)}
                            </div>
                        ))}
                    </div>
                )
            ))}
            {groupedOptions.ungrouped?.length > 0 && (
                <div className="option-group">
                    <div className="option-group-label">Other</div>
                    {groupedOptions.ungrouped.map((option, index) => (
                        <div
                            key={`ungrouped-${index}`}
                            className={`select-option ${option.value === value ? 'selected' : ''}`}
                            onClick={() => handleSelect(option.value)}
                        >
                            {renderOptionContent(option)}
                        </div>
                    ))}
                </div>
            )}
        </div>
    ) : (
        <>
            {options.map((option, index) => (
                <div
                    key={`option-${index}`}
                    className={`select-option ${option.value === value ? 'selected' : ''} ${option.is_default ? 'default' : ''}`}
                    onClick={() => handleSelect(option.value)}
                >
                    {renderOptionContent(option)}
                </div>
            ))}
        </>
    );

    return (
        <BaseDropdown
            toggleContent={
                <div className="select-toggle-content">
                    <span className="select-label">{selectedOption?.label || placeholder}</span>
                    {selectedOption?.metadata?.leftInfo && (
                        <div
                            className="select-left-info"
                            dangerouslySetInnerHTML={{ __html: selectedOption.metadata.leftInfo }}
                        />
                    )}
                    {selectedOption?.metadata?.rightInfo && (
                        <span className="select-right-info">{selectedOption.metadata.rightInfo}</span>
                    )}
                    {selectedOption?.is_default && groups && (
                        <Lock size={14} className="lock-icon" />
                    )}
                </div>
            }
            menuContent={menuContent}
            disabled={disabled || options.length === 0}
            className={className}
            forceOpen={isOpen}
            onOpenChange={setIsOpen}
        />
    );
};